/**
 * Guest Wizard State Management
 *
 * Sparar wizard-state i localStorage för gäster (ej inloggade).
 * Data sparas med 7 dagars utgångsdatum.
 */

import type { ParsedSieData, Calculations } from "./api";

const STORAGE_KEY = "kvanten_guest_wizard";
const EXPIRY_DAYS = 7;

export interface GuestBoardMember {
  id: string; // local-only ID
  name: string;
  role: string;
  personalId: string | null;
}

/**
 * Company data defined by guest in Step 1 (before SIE upload)
 */
export interface GuestCompany {
  organizationNumber: string;
  name: string;
  type: "AB" | "EF" | "HB" | "KB";
  seatMunicipality?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  /** Whether data was fetched from Bolagsverket */
  fromLookup: boolean;
}

export interface GuestWizardState {
  /** Company defined in Step 1 (before SIE upload) */
  company: GuestCompany | null;
  parsedSieData: ParsedSieData | null;
  calculations: Calculations | null;
  userInput: Record<string, unknown>;
  boardMembers: GuestBoardMember[];
  /** @deprecated Use company instead - kept for backward compatibility */
  companyInfo: {
    name: string;
    organizationNumber: string;
  } | null;
  fiscalYear: {
    start: string;
    end: string;
  } | null;
  currentStep: number;
  savedAt: number; // timestamp
}

const defaultState: GuestWizardState = {
  company: null,
  parsedSieData: null,
  calculations: null,
  userInput: {},
  boardMembers: [],
  companyInfo: null,
  fiscalYear: null,
  currentStep: 1,
  savedAt: Date.now(),
};

/**
 * Save guest wizard state to localStorage
 */
export function saveGuestWizard(
  data: Partial<Omit<GuestWizardState, "savedAt">>
): void {
  try {
    const existing = loadGuestWizard();
    const updated: GuestWizardState = {
      ...defaultState,
      ...existing,
      ...data,
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // localStorage might be full or unavailable
    console.warn("Could not save guest wizard state");
  }
}

/**
 * Load guest wizard state from localStorage.
 * Returns null if expired or not found.
 */
export function loadGuestWizard(): GuestWizardState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const state: GuestWizardState = JSON.parse(raw);

    // Check expiry
    const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - state.savedAt > expiryMs) {
      clearGuestWizard();
      return null;
    }

    return state;
  } catch {
    return null;
  }
}

/**
 * Clear guest wizard state from localStorage
 */
export function clearGuestWizard(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Check if there is a valid guest wizard session
 */
export function hasGuestWizardSession(): boolean {
  return loadGuestWizard() !== null;
}
